import React from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Container } from '@mui/material';
import PublicSiteHeader from '@components/layout/PublicSiteHeader';
import SiteFooter from '@components/layout/SiteFooter';
import ContestCatalogExperience from '@components/contest/ContestCatalogExperience';
import { contestTabFromQuery } from '@components/contest/ContestJourneyChrome';
import { brandColors } from '@config/brandTokens';
import { isReleaseSport } from '@config/releaseProfile';
import { toSportLabel } from '@utils/sports';
import { useActiveSession } from '@hooks/useActiveSession';
import { isPlatformAdminUser } from '@utils/authRouting';
import { mapPublicContestUrlToPortal } from '@utils/contestSubmitNavigation';

const PublicContestPage: React.FC = () => {
  const { sport: sportSlug } = useParams<{ sport: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { signedIn, user } = useActiveSession();
  const sportName = toSportLabel(sportSlug);
  const allowed = isReleaseSport(sportName);

  const ageFromQuery = searchParams.get('age') || undefined;
  const tabFromQuery = contestTabFromQuery(searchParams.get('tab'));
  const contestFromQuery = searchParams.get('contest');

  if (signedIn && user && !isPlatformAdminUser(user)) {
    const dest = mapPublicContestUrlToPortal(user, `${location.pathname}${location.search}`);
    return <Navigate to={dest} replace />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: brandColors.ice, display: 'flex', flexDirection: 'column' }}>
      <PublicSiteHeader activeSport={allowed ? sportName : undefined} />

      <Container maxWidth={false} sx={{ maxWidth: 1760, px: { xs: 1.5, md: 2 }, py: { xs: 1.5, md: 2 }, flex: 1 }}>
        {!allowed ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            EncycGames 1.0 contests are available for Football, Cricket and Badminton.
          </Alert>
        ) : (
          <ContestCatalogExperience
            sportName={sportName}
            initialAgeCategory={ageFromQuery}
            initialTab={tabFromQuery}
            initialContestId={contestFromQuery}
            uploadMode={searchParams.get('action') === 'upload'}
            playerId={signedIn ? user?.playerId : undefined}
            playerName={
              signedIn
                ? [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email
                : undefined
            }
            userId={signedIn ? user?.id : undefined}
          />
        )}
      </Container>
      <SiteFooter compact />
    </Box>
  );
};

export default PublicContestPage;
